import { useState, useEffect, useCallback } from 'react';

/**
 * How often a visible tab re-checks for a newer deploy. Long-lived tabs are the
 * whole point here, so this trades promptness for a negligible request rate;
 * the visibility and preload-error triggers below cover the urgent cases.
 */
const POLL_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Build id of the deploy currently live on the CDN, or null if we could not
 * find out (offline, or no version.json -- the dev server never writes one).
 */
async function fetchDeployedBuildId(signal: AbortSignal): Promise<string | null> {
  try {
    // A static file on our own origin, written by the build (see
    // buildVersionPlugin in vite.config.ts). Not a backend endpoint, so the
    // generated API clients deliberately are not involved.
    const response = await fetch('/version.json', { cache: 'no-store', signal });

    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as { buildId?: unknown };

    return typeof body.buildId === 'string' ? body.buildId : null;
  } catch {
    return null;
  }
}

/**
 * Notices when a newer build has been deployed under a tab that is still
 * running an older one.
 *
 * index.html names the hashed bundle URLs of exactly one build, so a tab left
 * open across a deploy keeps asking for that build's chunks. Those survive for
 * a while now (deploys no longer delete the previous build, and superseded
 * bundles age out on a lifecycle rule), but "a while" is not forever, and a tab
 * running week-old code is talking to an API that has moved on regardless.
 *
 * The recovery path this replaces was a forced reload at the moment a chunk
 * failed, which threw away whatever was on screen -- a half-written ticket
 * comment, an unsaved canvas edit. This surfaces the same information as an
 * offer instead, and lets the reload happen when the user is ready for it.
 */
export function useAppUpdate() {
  const [pendingBuildId, setPendingBuildId] = useState<string | null>(null);
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null);

  useEffect(() => {
    // The dev server has no version.json, and its module graph is live anyway.
    if (__BUILD_ID__ === 'dev') {
      return undefined;
    }

    const controller = new AbortController();

    const check = async () => {
      // Background tabs poll nothing; visibilitychange picks them back up.
      if (document.visibilityState !== 'visible') {
        return;
      }

      const deployedBuildId = await fetchDeployedBuildId(controller.signal);

      if (deployedBuildId && deployedBuildId !== __BUILD_ID__) {
        setPendingBuildId(deployedBuildId);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    // Vite fires this when a lazily-imported chunk cannot be fetched. On a
    // long-lived tab that usually means our build is gone from the CDN, so
    // confirm against version.json rather than waiting for the next poll. A
    // merely flaky network resolves to the same build id and shows nothing.
    const handlePreloadError = () => {
      void check();
    };

    void check();

    const timer = setInterval(() => void check(), POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('vite:preloadError', handlePreloadError);

    return () => {
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('vite:preloadError', handlePreloadError);
    };
  }, []);

  const dismiss = useCallback(() => {
    setDismissedBuildId(pendingBuildId);
  }, [pendingBuildId]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    // Dismissal is per build, so a deploy landing after one was waved off asks
    // again rather than staying silent for the life of the tab.
    isUpdateAvailable: pendingBuildId !== null && pendingBuildId !== dismissedBuildId,
    dismiss,
    reload,
  };
}
